<script setup>
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { useVModel } from "@vueuse/core";
import { computed, reactive, ref, toRefs, watch } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toast-notification";
import Checkbox from "@/components/forms/SharedComponents/DarkBlueInputs/Checkbox.vue";
import Spinner from "@/components/helpers/Spinner.vue";
import { generateStudentName } from "@/components/StudentFulfillment/components/MainPage/utils";
import { ERROR_MESSAGE } from "@/components/StudentFulfillment/components/modals/HousingFulfillmentModal/constants";
import FeedbackIcon from "@/components/svg-icons/FeedbackIcon";
import { isAuthorized } from "@/composables/authorization";
import { ERROR_TIMEOUT, PERMISSIONS, SUCCESS_TIMEOUT } from "@/constants";
import { formatDate, revertCamelCase } from "@/mixins/helpers.js";
import cipCodesService from "@/services/cipCodes";
import ordersService from "@/services/orders";
import profileService from "@/services/profile";

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  modelValue: {
    type: Object,
    required: true,
  },
  orderLoading: {
    type: Boolean,
    required: true,
  },
});
const { data } = toRefs(props);
const studentInfoUpdateData = reactive({
  financiallyCleared: data.value?.order?.financially_cleared,
  needsAdvising: undefined,
  requiresAccommodations: undefined,
});
const loadingSaveCheckboxes = ref(false);
const disableCheckboxes = ref(false);
const yearInSchool = ref("");
const emit = defineEmits(["update:modelValue"]);
const updateData = useVModel(props, "modelValue", emit);
const route = useRoute();
const toast = useToast();

const canUpdateAdvisingFlags = computed(() => {
  return isAuthorized(PERMISSIONS.USERS_ADVISE);
});
const canUpdateFinanciallyClear = computed(() => {
  return isAuthorized(PERMISSIONS.ORDERS_WRITE_ADMIN);
});

const needsAdvisingOrRequiresAccomodation = computed(
  () => studentInfoUpdateData.requiresAccommodations || studentInfoUpdateData.needsAdvising,
);

const eligibleForMemberScholarships = computed(
  () => profileData?.value?.data?.financial_aid?.[0]?.eligible_for_member_scholarships,
);

const queryClient = useQueryClient();

const participantId = computed(() => data.value?.order?.participant_id);

const { data: profileData, isLoading: profileLoading } = useQuery({
  queryKey: ["profileData", participantId.value],
  queryFn: () => profileService.getProfile(participantId.value).then((profile) => profile?.data || {}),
  enabled: computed(() => !!participantId.value),
});

const enableMajors = computed(() => profileData.value?.data?.majors?.length > 0);

const profileMajorsCipCodes = computed(() => profileData.value?.data?.majors?.map(({ cip_code }) => cip_code) ?? []);

const { data: majorsData, isFetching: majorsLoading } = useQuery({
  queryKey: ["majors", participantId.value, ...profileMajorsCipCodes.value],
  queryFn: () =>
    cipCodesService
      .listObj({
        extraParams: {
          cip_codes: profileMajorsCipCodes.value,
        },
      })
      .then((val) => {
        return (
          val.map((ele) => ({
            ...ele,
            id: ele.cip_code,
            name: ele.cip_title,
          })) || []
        );
      }),
  enabled: enableMajors,
});

const mutationOrderPartially = useMutation({
  mutationFn: () =>
    ordersService.updateOrderPartially({
      order_id: route.params?.orderId,
      payload: {
        financially_cleared: studentInfoUpdateData.financiallyCleared,
      },
    }),
  onSuccess: () => {
    updateData.value.financially_cleared = studentInfoUpdateData.financiallyCleared;
    queryClient.invalidateQueries({
      queryKey: [
        "order",
        route.params?.orderId,
        {
          get_canceled: true,
          get_withdrawn: true,
        },
      ],
    });
    toast.open({
      message: "Order Saved Successfully",
      type: "success",
      position: "bottom",
      duration: SUCCESS_TIMEOUT,
    });
  },
  onError: () => {
    studentInfoUpdateData.financiallyCleared = !studentInfoUpdateData.financiallyCleared;
  },
});

const mutationProfilePartially = useMutation({
  mutationFn: () =>
    profileService.updatePartiallyProfile(data.value?.order?.participant_id, {
      needs_advising: studentInfoUpdateData.needsAdvising,
      requires_accommodations: studentInfoUpdateData.requiresAccommodations,
    }),
  onSuccess: () => {
    queryClient.invalidateQueries({
      queryKey: ["profileData", participantId.value],
    });
    toast.open({
      message: "Order Saved Successfully",
      type: "success",
      position: "bottom",
      duration: SUCCESS_TIMEOUT,
    });
  },
  onError: () => {
    studentInfoUpdateData.needsAdvising = profileData.value?.data?.needs_advising;
    studentInfoUpdateData.requiresAccommodations = profileData.value?.data?.requires_accommodations;
  },
});

const isLoading = computed(
  () => profileLoading.value || props.orderLoading || loadingSaveCheckboxes.value || majorsLoading.value,
);

const majors = computed(() => majorsData.value?.map(({ name }) => name)?.join(", "));

watch(
  profileData,
  (value) => {
    studentInfoUpdateData.needsAdvising = value?.data?.needs_advising;
    studentInfoUpdateData.requiresAccommodations = value?.data?.requires_accommodations;
    const contentText = value?.data?.year_in_school_at_time_of_api_program;
    yearInSchool.value = contentText ? revertCamelCase(contentText) : "";
  },
  { deep: true },
);

const normalize = (val) => {
  if (val === true || val === "true") return true;
  return false; // everything else (false, "false", null, undefined) → false
};

watch(studentInfoUpdateData, async ({ financiallyCleared, needsAdvising, requiresAccommodations }) => {
  disableCheckboxes.value = true;
  loadingSaveCheckboxes.value = true;
  try {
    if (!props.orderLoading && financiallyCleared !== data.value?.order?.financially_cleared)
      await mutationOrderPartially.mutateAsync();

    if (needsAdvising === undefined && requiresAccommodations === undefined) return;
    if (
      !profileLoading.value &&
      (normalize(needsAdvising) !== profileData.value?.data?.needs_advising ||
        normalize(requiresAccommodations) !== profileData.value?.data?.requires_accommodations)
    )
      await mutationProfilePartially.mutateAsync();
  } catch (error) {
    const errorMessage = error.cause?.data?.details?.detail ?? ERROR_MESSAGE;

    toast.open({
      message: errorMessage,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  } finally {
    disableCheckboxes.value = false;
    loadingSaveCheckboxes.value = false;
  }
});
watch(
  () => data.value?.order?.financially_cleared,
  (newVal, oldVal) => {
    if (newVal === oldVal) return;
    studentInfoUpdateData.financiallyCleared = newVal;
  },
);
</script>

<script>
export default {
  name: "StudentInfo",
};
</script>

<template>
  <div class="relative">
    <div v-if="isLoading">
      <Spinner class="absolute h-full w-full bg-transparent" />
    </div>
    <div
      :class="[
        'sm:grid sm:grid-cols-3 space-y-4',
        isLoading ? 'opacity-50' : '',
      ]"
    >
      <div>
        <p
          id="student-name-label"
          dataTestId="student-name-label"
          class="mb-1 font-bold"
        >
          Name
        </p>
        <div
          v-if="isLoading"
          id="loading-component"
          dataTestId="loading-component"
          class="flex animate-pulse items-center"
        >
          <div class="h-4 w-3/6 bg-blue-400"></div>
        </div>
        <div v-else class="flex">
          <p id="student-name-data" dataTestId="student-name-data">
            {{ generateStudentName(data?.order) }}
          </p>
          <FeedbackIcon
            v-if="needsAdvisingOrRequiresAccomodation"
            :class="[
              'relative bottom-1 ml-2 h-9 w-9',
              studentInfoUpdateData.requiresAccommodations
                ? 'fill-current text-error-900'
                : 'fill-current text-error-900 opacity-25',
            ]"
          />
        </div>
      </div>
      <div>
        <p
          id="student-name-label"
          dataTestId="student-name-label"
          class="mb-1 font-bold"
        >
          Preferred Name
        </p>
        <div
          v-if="isLoading"
          id="loading-component"
          dataTestId="loading-component"
          class="flex animate-pulse items-center"
        >
          <div class="h-4 w-3/6 bg-blue-400"></div>
        </div>
        <p
          v-else
          id="student-preferred-name-data"
          class="capitalize"
          dataTestId="student-preferred-name-data"
        >
          {{ profileData?.data?.preferred_first_name }}
        </p>
      </div>
      <div>
        <p
          id="student-name-label"
          dataTestId="student-name-label"
          class="mb-1 font-bold"
        >
          Pronouns
        </p>
        <div
          v-if="isLoading"
          id="loading-component"
          dataTestId="loading-component"
          class="flex animate-pulse items-center"
        >
          <div class="h-4 w-3/6 bg-blue-400"></div>
        </div>
        <p
          v-else
          id="student-pronouns-data"
          class="capitalize"
          dataTestId="student-pronouns-data"
        >
          {{ profileData?.data?.pronouns }}
        </p>
      </div>
      <div>
        <p
          id="student-email-label"
          dataTestId="student-email-label"
          class="mb-1 font-bold"
        >
          Email
        </p>
        <div
          v-if="isLoading"
          id="loading-component"
          dataTestId="loading-component"
          class="flex animate-pulse items-center"
        >
          <div class="h-4 w-3/6 bg-blue-400"></div>
        </div>
        <p v-else id="student-email-data" dataTestId="student-email-data">
          {{ profileData?.data?.email }}
        </p>
      </div>
      <div>
        <p
          id="student-phone-number-label"
          dataTestId="student-phone-number-label"
          class="mb-1 font-bold"
        >
          Phone #
        </p>
        <div
          v-if="isLoading"
          id="loading-component"
          dataTestId="loading-component"
          class="flex animate-pulse items-center"
        >
          <div class="h-4 w-3/6 bg-blue-400"></div>
        </div>
        <p
          v-else
          id="student-phone-number-data"
          dataTestId="student-phone-number-data"
        >
          {{ profileData?.data?.phone_number }}
        </p>
      </div>
      <div>
        <p
          id="birthdate-label"
          dataTestId="birthdate-label"
          class="mb-1 font-bold"
        >
          Birthday
        </p>
        <div
          v-if="isLoading"
          id="loading-component"
          dataTestId="loading-component"
          class="flex animate-pulse items-center"
        >
          <div class="h-4 w-3/6 bg-blue-400"></div>
        </div>
        <p v-else id="birthdate-data" dataTestId="birthdate-data">
          {{ formatDate(profileData?.data?.birthdate) }}
        </p>
      </div>
      <div class="col-start-1">
        <p
          id="home-institution-label"
          dataTestId="home-institution-label"
          class="mb-1 font-bold"
        >
          Home University
        </p>
        <div
          v-if="isLoading"
          id="loading-component"
          dataTestId="loading-component"
          class="flex animate-pulse items-center"
        >
          <div class="h-4 w-3/6 bg-blue-400"></div>
        </div>
        <p v-else id="home-institution-data" dataTestId="home-institution-data">
          {{ props.data?.order?.home_institution_name }}
        </p>
      </div>
      <div>
        <p
          id="student-major-label"
          dataTestId="student-major-label"
          class="mb-1 font-bold"
        >
          Major
        </p>
        <div
          v-if="isLoading"
          id="loading-component"
          dataTestId="loading-component"
          class="flex animate-pulse items-center"
        >
          <div class="h-4 w-3/6 bg-blue-400"></div>
        </div>
        <p v-else id="student-major-data" dataTestId="student-major-data">
          {{ majors }}
        </p>
      </div>
      <div>
        <p
          id="year-in-school-label"
          dataTestId="year-in-school-label"
          class="mb-1 font-bold"
        >
          Year In School
        </p>
        <div
          v-if="isLoading"
          id="loading-component"
          dataTestId="loading-component"
          class="flex animate-pulse items-center"
        >
          <div class="h-4 w-3/6 bg-blue-400"></div>
        </div>
        <p v-else id="year-in-school-data" dataTestId="year-in-school-data">
          {{ yearInSchool }}
        </p>
      </div>
      <div>
        <p
          id="application-id-label"
          dataTestId="application-id-label"
          class="mb-1 font-bold"
        >
          Application ID
        </p>
        <div
          v-if="isLoading"
          id="loading-component"
          dataTestId="loading-component"
          class="flex animate-pulse items-center"
        >
          <div class="h-4 w-3/6 bg-blue-400"></div>
        </div>
        <p v-else id="application-id-data" dataTestId="application-id-data">
          {{ props.data?.order?.application_id }}
        </p>
      </div>
      <div>
        <p
          id="application-version-label"
          dataTestId="application-version-label"
          class="mb-1 font-bold"
        >
          Application Version
        </p>
        <div
          v-if="isLoading"
          id="loading-component"
          dataTestId="loading-component"
          class="flex animate-pulse items-center"
        >
          <div class="h-4 w-3/6 bg-blue-400"></div>
        </div>
        <p
          v-else
          id="application-version-data"
          dataTestId="application-version-data"
          class="uppercase"
        >
          {{ props.data?.order?.application_version }}
        </p>
      </div>
      <div v-if="eligibleForMemberScholarships">
        <p
          id="financially-cleared-label"
          dataTestId="financially-cleared-label"
          class="mb-1 font-bold"
        >
          Eligible for Member Scholarship
        </p>
        <Checkbox
          id="eligible_for_member_scholarships"
          v-model="eligibleForMemberScholarships"
          :is-disabled="true"
        />
      </div>
      <div class="col-start-1">
        <p
          id="financially-cleared-label"
          dataTestId="financially-cleared-label"
          class="mb-1 font-bold"
        >
          Financially Clear
        </p>
        <Checkbox
          id="financially_cleared"
          v-model="studentInfoUpdateData.financiallyCleared"
          class="w-fit"
          :is-disabled="!canUpdateFinanciallyClear || disableCheckboxes"
        />
      </div>
      <div class="col-start-2">
        <p
          id="needs-advising-label"
          dataTestId="needs-advising-label"
          class="mb-1 font-bold"
        >
          Needs Advising
        </p>
        <Checkbox
          id="needs_advising"
          v-model="studentInfoUpdateData.needsAdvising"
          class="w-fit"
          :is-disabled="!canUpdateAdvisingFlags || disableCheckboxes"
        />
      </div>
      <div class="col-start-3">
        <p
          id="requires-accommodations-label"
          dataTestId="requires-accommodationsg-label"
          class="mb-1 font-bold"
        >
          Requires Accommodations
        </p>
        <Checkbox
          id="requires_accommodations"
          v-model="studentInfoUpdateData.requiresAccommodations"
          class="w-fit"
          :is-disabled="!canUpdateAdvisingFlags || disableCheckboxes"
        />
      </div>
    </div>
  </div>
</template>
